import { useMemo } from 'react';

import { SelectOption, Account } from '~/shared/utils/types';
import SidePanel from '~/shared/components/SidePanel';
import FilterPanelHeader from '~/shared/components/filters/FilterPanelHeader';

import {
  useVolumeFilterState,
  panelStateHasActiveFilters,
  VolumeFilterPanelContent,
  type AppliedVolumeFilters,
  type CapacityBounds,
} from '~/shared/components/filters/volumeFilters';

export type GroupFleetManagerSelectionOption = {
  label: string;
  options: SelectOption[];
};

interface AllVolumesFiltersSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResetFilters: () => void;
  /** Everything already applied via the URL — the panel seeds pending state from this. */
  applied: AppliedVolumeFilters;
  /** Endpoints for the capacity slider; see `useVolumeCapacityBounds`. */
  capacityBounds?: CapacityBounds;
  isOrgView?: boolean;
  accounts?: Account[];
  activeOrgId?: string;
}

export default function AllVolumesFiltersSidePanel({
  open,
  onOpenChange,
  onResetFilters,
  applied,
  capacityBounds,
  isOrgView = false,
  accounts = [],
  activeOrgId,
}: AllVolumesFiltersSidePanelProps) {
  const filterState = useVolumeFilterState({ open, capacityBounds, ...applied });

  const hasActiveFilters = panelStateHasActiveFilters(filterState.pending);

  const handleClose = () => {
    filterState.applyPendingFilters();
  };

  const filterableAccounts = useMemo(() => {
    if (!activeOrgId) {
      return accounts;
    }
    return accounts.filter((account) => account.accountId !== activeOrgId);
  }, [accounts, activeOrgId]);

  const handleResetFilters = () => {
    filterState.resetPendingFilters();
    onResetFilters();
    onOpenChange(false);
  };

  return (
    <SidePanel open={open} onOpenChange={onOpenChange} width="w-[445px]" onClose={handleClose}>
      <div className="flex flex-col h-full">
        <FilterPanelHeader
          onClearAll={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          onClose={() => {
            handleClose();
            onOpenChange(false);
          }}
        />

        <VolumeFilterPanelContent
          panelOpen={open}
          pending={filterState.pending}
          setField={filterState.setField}
          setCapacityMin={filterState.setCapacityMin}
          setCapacityMax={filterState.setCapacityMax}
          clearCapacity={filterState.clearCapacity}
          isOrgView={isOrgView}
          accounts={filterableAccounts}
        />
      </div>
    </SidePanel>
  );
}
